import React, { useEffect, useState } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import moment from 'moment';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import '../../css/financeiro-parcela-pagar.css';
import { ehSomenteVisualizacao } from '../Orcamento/somenteVisualizacaoPolicy';

export default function OnePageEvento({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const [evento, setEvento] = useState(null);
    const [orcamento, setOrcamento] = useState(null);
    const [itensResumo, setItensResumo] = useState([]);
    const [itensOrcamento, setItensOrcamento] = useState([]);
    const [subitensOrcamento, setSubitensOrcamento] = useState([]);
    const [totalOrcamento, setTotalOrcamento] = useState(0);
    const [rsvp, setRsvp] = useState({ total: 0, confirmados: 0, pendentes: 0, declinados: 0, outros: 0 });
    const [timelineConfirmacoes, setTimelineConfirmacoes] = useState([]);
    const [passagensEmitidas, setPassagensEmitidas] = useState(0);
    const [pendentesAereo, setPendentesAereo] = useState(0);
    const [totalND, setTotalND] = useState(0);
    const [totalNF, setTotalNF] = useState(0);
    const [loadingOrcamento, setLoadingOrcamento] = useState(true);

    const selecionarOrcamento = (lista) => {
        if (!Array.isArray(lista) || lista.length === 0) return null;
        if (lista.length === 1) return lista[0];
        // Prioriza orçamentos com aprovação (financeiro ou diretor), pegando o mais recente
        const comAprovacao = lista
            .filter(o => o.dataAprovacao || o.dataAprovacaoDiretor)
            .sort((a, b) => {
                const daA = a.dataAprovacao ? new Date(a.dataAprovacao).getTime() : 0;
                const ddA = a.dataAprovacaoDiretor ? new Date(a.dataAprovacaoDiretor).getTime() : 0;
                const maxA = Math.max(daA, ddA);
                const daB = b.dataAprovacao ? new Date(b.dataAprovacao).getTime() : 0;
                const ddB = b.dataAprovacaoDiretor ? new Date(b.dataAprovacaoDiretor).getTime() : 0;
                const maxB = Math.max(daB, ddB);
                return maxB - maxA; // desc
            });
        if (comAprovacao.length > 0) return comAprovacao[0];
        // Fallback: mais recente por data do orçamento
        return [...lista].sort((a, b) => new Date(b.data) - new Date(a.data))[0];
    };

    const montarResumoItens = (orc) => {
        if (!orc) { setItensResumo([]); return; }
        const itens = Array.isArray(orc.orcamentoitem) ? orc.orcamentoitem : [];
        const linhas = itens.map(item => {
            // Opção do orçamento (D-0113): não soma nem tem fornecedor a pagar.
            const subitens = (Array.isArray(item.subitem) ? item.subitem : []).filter((s) => !ehSomenteVisualizacao(s));
            const valorPreEvento = subitens.reduce((acc, s) => acc + (parseFloat(s.totalPreEvento || 0) || 0), 0);
            const valorNegociado = subitens.reduce((acc, s) => acc + (parseFloat(s.totalNegociado || 0) || 0), 0);
            const valorFechamento = subitens.reduce((acc, s) => acc + (parseFloat(s.totalFechamento || 0) || 0), 0);
            const fornecedoresUnicos = [];
            const nomesSet = new Set();
            subitens.forEach(s => {
                const nome = s.fornecedor?.nome || s.fornecedorpagamento?.nome || null;
                if (nome && !nomesSet.has(nome)) { nomesSet.add(nome); fornecedoresUnicos.push(nome); }
            });
            return {
                codigo: item.produto?.codigo || '',
                descricao: item.produto?.nome || 'Item',
                fornecedores: fornecedoresUnicos.join(', '),
                valorPreEvento,
                valorNegociado,
                valorFechamento,
            };
        });
        setItensResumo(linhas);
    };

    const carregar = async () => {
        try {
            setLoadingOrcamento(true);
            const ev = await api({ method: 'get', action: '/evento/' + id, token });
            setEvento(ev.data.data);

            // Busca estatísticas de participantes
            const estatisticasResp = await api({ method: 'get', action: `/evento/estatisticasParticipantes/${id}`, token });
            if (estatisticasResp?.data?.data) {
                setRsvp(estatisticasResp.data.data);
            }

            // Busca timeline de confirmações
            const timelineResp = await api({ method: 'get', action: `/evento/timelineConfirmacoes/${id}`, token });
            if (timelineResp?.data?.data) {
                setTimelineConfirmacoes(timelineResp.data.data);
            }

            // Busca participantes com situação específica (passagens emitidas)
            const passagensResp = await api({ method: 'get', action: `/evento/participantesPorSituacao/${id}/550581161651775386`, token });
            if (passagensResp?.data?.data) {
                setPassagensEmitidas(passagensResp.data.data.total || 0);
            }

            // Busca participantes pendentes aéreo (situação 2)
            const pendentesAereoResp = await api({ method: 'get', action: `/evento/participantesPorSituacao/${id}/2`, token });
            if (pendentesAereoResp?.data?.data) {
                setPendentesAereo(pendentesAereoResp.data.data.total || 0);
            }

            // Busca orçamentos do evento
            const respLista = await api({ method: 'get', action: '/orcamento', token, params: { evento: id } });
            const lista = respLista?.data?.data || respLista?.data || [];
            const escolhido = selecionarOrcamento(lista);
            if (!escolhido) {
                setLoadingOrcamento(false);
                return;
            }

            // Carrega orçamento escolhido com itens e subitens (já vem tudo junto)
            const respOrc = await api({ method: 'get', action: '/orcamento/' + escolhido.id, token });
            const orcamento = respOrc?.data?.data || respOrc?.data;
            if (!orcamento) {
                setLoadingOrcamento(false);
                return;
            }

            // Extrai itens e subitens do orçamento (já vem no show)
            const itens = Array.isArray(orcamento.orcamentoitem) ? orcamento.orcamentoitem : [];
            setItensOrcamento(itens);

            // Monta o resumo para exibição
            montarResumoItens(orcamento);

            // Extrai todos os subitens dos itens
            const subitens = itens.flatMap(item => Array.isArray(item.subitem) ? item.subitem : []);
            setSubitensOrcamento(subitens);

            // Calcula totais
            const totalItens = itens.reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0);
            const totalSubitens = subitens.reduce((sum, subitem) => sum + (parseFloat(subitem.valor) || 0), 0);
            setTotalOrcamento(totalItens + totalSubitens);

            // Calcula totais por tipo de faturamento (ND e NF)
            let somaND = 0;
            let somaNF = 0;
            subitens.forEach(subitem => {
                if (ehSomenteVisualizacao(subitem)) return;
                const valorFechamento = parseFloat(subitem.totalFechamento || 0) || 0;
                if (subitem.tipofaturamento === 'nd') {
                    somaND += valorFechamento;
                } else if (subitem.tipofaturamento === 'nf') {
                    somaNF += valorFechamento;
                }
            });
            setTotalND(somaND);
            setTotalNF(somaNF);

            setLoadingOrcamento(false);

        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setLoadingOrcamento(false);
        }
    };

    useEffect(() => {
        carregar();
        // eslint-disable-next-line
    }, []);

    const fmtData = (d) => (d ? moment(d).format('DD/MM/YYYY') : 'A definir');
    const fmtHora = (h) => (h ? h : 'A definir');
    const fmtCapacidade = (n) => (n ? `${n} pessoas` : 'A definir');
    const situacaoTexto = (s) => (s ? s : 'A definir');

    const responsaveisAgencia = () => {
        const r = evento?.responsavel;
        if (!r) return 'A definir';
        if (Array.isArray(r)) return r.map((x) => x?.nome).filter(Boolean).join(', ') || 'A definir';
        return r?.nome || 'A definir';
    };

    const responsaveisCliente = () => {
        const r = evento?.responsavelcliente;
        if (!r) return 'A definir';
        if (Array.isArray(r)) return r.map((x) => x?.nome).filter(Boolean).join(', ') || 'A definir';
        return r?.nome || 'A definir';
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head" style={{ justifyContent: 'center', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div>
                        <h1 className="fin-pp__title">{evento?.nome ?? 'Gestão de Evento'}</h1>
                        {(evento?.empresa?.nome || evento?.cliente?.nome) && (
                            <p className="fin-pp__subtitle">{evento?.empresa?.nome ?? evento?.cliente?.nome}</p>
                        )}
                    </div>
                </div>

                {/* Informações Gerais */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Informações Gerais</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Empresa:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{evento?.empresa?.nome ?? 'A definir'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Cliente:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{evento?.cliente?.nome ?? 'A definir'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Situação:</span>
                                <span className="fin-pp__pill is-ok">{situacaoTexto(evento?.situacao)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Datas e Horários</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Data Inicial:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{fmtData(evento?.data)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Horário Inicial:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{fmtHora(evento?.hora)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Data Final:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{fmtData(evento?.dataFim)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Horário Final:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{fmtHora(evento?.horaFim)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Responsáveis</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Focal Point:</span>
                            <span style={{ color: 'var(--fin-mt)' }}>{responsaveisCliente()}</span>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Local do Evento</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Local:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{evento?.local ?? 'A definir'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Endereço:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{evento?.endereco ?? 'A definir'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 600, color: 'var(--fin-tx)' }}>Capacidade:</span>
                                <span style={{ color: 'var(--fin-mt)' }}>{fmtCapacidade(evento?.quantidadePessoas)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Orçamento do Evento */}
                <div className="fin-pp__card" style={{ overflow: 'hidden', marginBottom: 16 }}>
                    <div className="fin-pp__cphead" style={{ padding: '14px 20px', justifyContent: 'center' }}>
                        <span className="fin-pp__cphead-title">Orçamento do Evento</span>
                    </div>
                    <div style={{ padding: 20 }}>
                        {loadingOrcamento ? (
                            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--fin-mt)' }}>
                                <div className="fin-pp__spinner" role="status" aria-label="Carregando" />
                                <p style={{ marginTop: 8 }}>Carregando orçamento...</p>
                            </div>
                        ) : itensResumo.length === 0 ? (
                            <div style={{ color: 'var(--fin-mt)' }}>Nenhum orçamento encontrado para este evento.</div>
                        ) : (
                            <>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead>
                                            <tr>
                                                <th>Descrição de Custos</th>
                                                <th style={{ width: '40%' }}>Fornecedor</th>
                                                <th style={{ width: 140, textAlign: 'right' }}>Pré-evento (R$)</th>
                                                <th style={{ width: 140, textAlign: 'right' }}>Negociado (R$)</th>
                                                <th style={{ width: 160, textAlign: 'right' }}>Fechamento (R$)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itensResumo.map((lin, idx) => (
                                                <tr key={idx}>
                                                    <td>{lin.descricao}</td>
                                                    <td>{lin.fornecedores || 'A definir'}</td>
                                                    <td style={{ textAlign: 'right' }}>{(lin.valorPreEvento || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td style={{ textAlign: 'right' }}>{(lin.valorNegociado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td style={{ textAlign: 'right' }}>{(lin.valorFechamento || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Totais por Tipo de Faturamento */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
                                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 16, border: '1px solid var(--fin-acb)', background: 'var(--fin-acw)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: 'var(--fin-ac)', marginBottom: 4 }}>Total ND (Nota de Débito)</div>
                                                <small style={{ color: 'var(--fin-mt)' }}>Valores a serem repassados ao cliente</small>
                                            </div>
                                            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--fin-ac)' }}>
                                                R$ {totalND.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="fin-pp__card" style={{ flex: '1 1 280px', padding: 16, border: '1px solid #ffb74d', background: '#fff3e0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: 'var(--fin-wn)', marginBottom: 4 }}>Total NF (Nota Fiscal)</div>
                                                <small style={{ color: 'var(--fin-mt)' }}>Custos diretos da agência</small>
                                            </div>
                                            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--fin-wn)' }}>
                                                R$ {totalNF.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Controle de RSVP */}
                <div className="fin-pp__card" style={{ marginBottom: 16 }}>
                    <div className="fin-pp__cphead" style={{ padding: '14px 20px', justifyContent: 'center' }}>
                        <span className="fin-pp__cphead-title">Controle de RSVP - Convidados</span>
                    </div>
                    <div style={{ padding: 20 }}>
                        <div className="fin-pp__statusbar" style={{ flexWrap: 'wrap', gap: 12 }}>
                            {[
                                { titulo: 'Total Convidados', valor: rsvp.total },
                                { titulo: 'Confirmados', valor: rsvp.confirmados },
                                { titulo: 'Pendentes', valor: rsvp.pendentes },
                                { titulo: 'Declinados', valor: rsvp.declinados },
                                { titulo: 'Outros', valor: rsvp.outros },
                                { titulo: 'Passagens Emitidas', valor: passagensEmitidas },
                                { titulo: 'Pendentes Aéreo', valor: pendentesAereo },
                            ].map((c, i) => (
                                <div className="fin-pp__statuschip" key={i} style={{ flex: '1 1 140px', flexDirection: 'column', padding: '16px 12px', textAlign: 'center', gap: 4 }}>
                                    <span className="fin-pp__statuschip-num">{c.valor}</span>
                                    <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>{c.titulo}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Gráficos */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                    <div className="fin-pp__card" style={{ flex: '1 1 340px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Status dos Convites</span>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Confirmados', value: rsvp.confirmados || 0 },
                                        { name: 'Pendentes', value: rsvp.pendentes || 0 },
                                        { name: 'Declinados', value: rsvp.declinados || 0 },
                                        { name: 'Outros', value: rsvp.outros || 0 }
                                    ]}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {['#0088FE', '#00C49F', '#FFBB28', '#FF8042'].map((color, index) => (
                                        <Cell key={`cell-${index}`} fill={color} />
                                    ))}
                                </Pie>
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="fin-pp__card" style={{ flex: '1 1 340px', padding: 20 }}>
                        <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                            <span className="fin-pp__cphead-title">Timeline de Confirmações</span>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart
                                data={timelineConfirmacoes?.evento?.map(item => ({
                                    data: item.data || '',
                                    confirmados: item.presentes || 0
                                })) || []}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="data" />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="confirmados" stroke="#8884d8" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Controle de Cotas */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
                    <div className="fin-pp__card" style={{ flex: '1 1 340px', overflow: 'hidden' }}>
                        <div className="fin-pp__cphead" style={{ padding: '12px 16px' }}>
                            <span className="fin-pp__cphead-title">Controle de Cotas</span>
                        </div>
                        <div className="fin-pp__tblwrap">
                            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '60%' }}>Item</th>
                                        <th style={{ width: '40%', textAlign: 'center' }}>Quantidade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="fin-pp__cell-strong">Quantidade de Convidados</td>
                                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--fin-ac)' }}>
                                            {evento?.quantidadeConvidados || evento?.quantidadePessoas || 'Não informado'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fin-pp__cell-strong">Número de Hospedagens</td>
                                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--fin-ac)' }}>
                                            {evento?.evento_rsvp_data?.numero_hospedagens || 'Não informado'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fin-pp__cell-strong">Número de Inscrições</td>
                                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--fin-ac)' }}>
                                            {evento?.evento_rsvp_data?.numero_inscricoes || 'N/A'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
